import { memo } from "react";
import axios from "axios";
import { useQuery } from "react-query";

import { formatLedgerAmount } from "../../helpers/formatLedgerAmount";
import { getLedgerBalance } from "../../services/ledgerActivityServices";
import {
  LedgerActivityCurrency,
  LedgerBalance,
} from "../../types/ledgerActivity";
import { HTTP_CODES } from "../../constants";

import styles from "./index.module.scss";

type Props = {
  userId?: string;
  currency?: LedgerActivityCurrency;
};

const balanceErrorMessage = (error: unknown) => {
  if (
    axios.isAxiosError(error) &&
    error.response?.status === HTTP_CODES.NOT_FOUND
  ) {
    return "No ledger account for this currency.";
  }
  return "Balances are unavailable.";
};

const LedgerBalances = ({ userId, currency }: Props) => {
  const enabled = Boolean(userId && currency);
  const { isLoading, error, data } = useQuery<LedgerBalance, Error>(
    ["ledgerBalance", userId, currency],
    () => getLedgerBalance(userId!, currency!),
    { enabled }
  );

  if (!enabled) return null;

  return (
    <div className={styles.balances} data-testid="ledger-balances">
      {isLoading ? (
        <span className={styles.message}>Loading balances...</span>
      ) : error || !data ? (
        <span className={styles.message} data-testid="ledger-balances-error">
          {balanceErrorMessage(error)}
        </span>
      ) : (
        <>
          <div>
            <div className={styles.label}>Total balance</div>
            <div className={styles.value} data-testid="ledger-total-balance">
              {formatLedgerAmount(data.totalBalance, data.currency)}
            </div>
          </div>
          <div>
            <div className={styles.label}>Available balance</div>
            <div
              className={styles.value}
              data-testid="ledger-available-balance"
            >
              {formatLedgerAmount(data.availableBalance, data.currency)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default memo(LedgerBalances);
